import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { startRegistration } from '@simplewebauthn/browser';
import useSound from 'use-sound';
import sounds from '../sounds.mp3';
import { useAuth } from '../contexts/AuthContext';
import { sanitizeProfile } from '../sanitize';

const RECENT_PROFILES_KEY = 'recentProfiles';
const RECENT_PROFILES_LIMIT = 10;

// Recent history is a best-effort enhancement, so every failure degrades to an
// empty list: localStorage can be unavailable (privacy modes), full, or hold
// hand-edited junk, and none of that should surface as an error.
function readRecentProfiles() {
	try {
		const parsed = JSON.parse(window.localStorage.getItem(RECENT_PROFILES_KEY));
		if (!Array.isArray(parsed)) return [];
		const recents = [];
		const seen = new Set();
		for (const value of parsed) {
			if (typeof value !== 'string') continue;
			const name = value.trim();
			const key = name.toLowerCase();
			if (!name || key === 'help' || seen.has(key)) continue;
			seen.add(key);
			recents.push(name);
			if (recents.length === RECENT_PROFILES_LIMIT) break;
		}
		return recents;
	} catch {
		return [];
	}
}

// Moves `name` to the top of the history and returns the updated list so the
// caller can render it without re-reading storage.
function addRecentProfile(name) {
	const current = readRecentProfiles();
	if (typeof name !== 'string') return current;
	const normalized = name.trim();
	if (!normalized || normalized.toLowerCase() === 'help') return current;
	const updated = [
		normalized,
		...current.filter((item) => item.toLowerCase() !== normalized.toLowerCase()),
	].slice(0, RECENT_PROFILES_LIMIT);
	try {
		window.localStorage.setItem(RECENT_PROFILES_KEY, JSON.stringify(updated));
	} catch {
		// Viewing a profile still succeeds when the history can't be stored.
	}
	return updated;
}

export default function BuddyInfo() {
	const soundMap = {
		register: [2300, 2000],
		doorOpen: [5000, 1500],
		doorClose: [6800, 1000],
		msgRcv: [8600, 1000],
		msgSnd: [10300, 1000],
		moo: [12200, 1000],
		bell: [14000, 1000],
		whip: [15600, 500],
		ring: [16700, 2200],
		firstMsg: [19000, 1000],
		connect: [20800, 1000],
		disconnect: [22200, 1000],
		error: [23700, 1000],
	};

	const [play] = useSound(sounds, {
		interrupt: true,
		sprite: soundMap,
	});

	const { screenName: routeScreenName } = useParams();
	const navigate = useNavigate();
	const { user, token, logout } = useAuth();

	const [query, setQuery] = useState(routeScreenName || '');
	const [recents, setRecents] = useState(readRecentProfiles);
	const [isOpen, setIsOpen] = useState(false);
	const [profile, setProfile] = useState(null);
	const [error, setError] = useState('');
	const [loading, setLoading] = useState(false);
	const [passkeyStatus, setPasskeyStatus] = useState(null);
	const [registeringPasskey, setRegisteringPasskey] = useState(false);
	const fieldRef = useRef(null);
	const toggleRef = useRef(null);
	const inputRef = useRef(null);

	const loadProfile = useCallback((name) => {
		setLoading(true);
		setError('');
		fetch(`/api/profile/${encodeURIComponent(name)}`)
			.then((r) => {
				if (!r.ok) throw new Error('Not found');
				return r.json();
			})
			.then((data) => {
				setProfile(data);
				setQuery(data.screen_name);
				setRecents(addRecentProfile(data.screen_name));
				play({ id: 'doorOpen' });
			})
			.catch(() => {
				setError('Screen name not found');
				setProfile(null);
				play({ id: 'error' });
			})
			.finally(() => setLoading(false));
	}, [play]);

	// Load profile from route param
	useEffect(() => {
		if (routeScreenName) {
			loadProfile(routeScreenName);
		}
	}, [routeScreenName, loadProfile]);

	// The same BuddyInfo instance is reused across /profile/:screenName routes,
	// so reset per-view state when the route changes. Otherwise leaving the help
	// profile and coming back re-displays a stale "registered" message, and a
	// dropdown left open would hang over the profile that just opened.
	useEffect(() => {
		setPasskeyStatus(null);
		setIsOpen(false);
	}, [routeScreenName]);

	// Standard dismissal for an open dropdown. Bound only while open so there is
	// no document listener sitting around for the usual closed case.
	useEffect(() => {
		if (!isOpen) return undefined;
		const closeOnEscape = (e) => {
			// Focus would otherwise fall back to the document when the list
			// unmounts from under it.
			if (e.key === 'Escape') {
				setIsOpen(false);
				toggleRef.current?.focus();
			}
		};
		const closeOnOutsideClick = (e) => {
			if (!fieldRef.current?.contains(e.target)) setIsOpen(false);
		};
		document.addEventListener('keydown', closeOnEscape);
		document.addEventListener('mousedown', closeOnOutsideClick);
		return () => {
			document.removeEventListener('keydown', closeOnEscape);
			document.removeEventListener('mousedown', closeOnOutsideClick);
		};
	}, [isOpen]);

	// The dropdown lists locally remembered profiles instead of the old server
	// autocomplete. It renders only while the user holds it open, so it can
	// never flash over a profile mid-navigation. An empty search matches
	// everything, which is exactly the full recent list.
	const search = query.trim().toLowerCase();
	const suggestions = recents.filter((name) => name.toLowerCase().includes(search));

	const handleSearch = (e) => {
		e?.preventDefault();
		if (query.trim()) {
			setIsOpen(false);
			play({ id: 'firstMsg' });
			navigate(`/profile/${encodeURIComponent(query.trim())}`);
		}
	};

	const clearLookup = () => {
		setQuery('');
		setIsOpen(false);
		setProfile(null);
		setError('');
		navigate('/');
	};

	const handleQueryChange = (e) => {
		const value = e.target.value;
		setQuery(value);
		setProfile(null);
		setError('');
		if (!value) {
			navigate('/');
		}
	};

	// Suggestions come from readRecentProfiles, so they are already trimmed.
	const selectSuggestion = (name) => {
		setQuery(name);
		setIsOpen(false);
		// Selecting from the list unmounts the button that had focus, so put it
		// back on the field the list belongs to.
		inputRef.current?.focus();
		navigate(`/profile/${encodeURIComponent(name)}`);
	};

	// Account age (SQLite CURRENT_TIMESTAMP is UTC, append Z for correct parsing)
	const getOnlineTime = (createdAt) => {
		if (!createdAt) return '0 minutes';
		const utcDate = createdAt.endsWith('Z') ? createdAt : createdAt + 'Z';
		const diff = Date.now() - new Date(utcDate).getTime();
		if (diff < 0) return '0 minutes';
		const days = Math.floor(diff / (1000 * 60 * 60 * 24));
		if (days > 365) return `${Math.floor(days / 365)} year(s), ${days % 365} day(s)`;
		if (days > 0) return `${days} day(s)`;
		const hours = Math.floor(diff / (1000 * 60 * 60));
		if (hours > 0) return `${hours} hour(s)`;
		const mins = Math.floor(diff / (1000 * 60));
		return `${mins} minute(s)`;
	};

	// Prefer the route param while the profile is still loading so the footer
	// doesn't briefly show "View My Profile" on your own profile route.
	const activeScreenName = profile ? profile.screen_name : routeScreenName;
	const isOwnProfile = Boolean(
		user && activeScreenName && user.screen_name.toLowerCase() === activeScreenName.toLowerCase()
	);
	// The help profile doubles as the place to enroll a passkey for a new
	// domain, since passkeys are bound to the domain they were created on.
	const isHelpProfile = Boolean(activeScreenName && activeScreenName.toLowerCase() === 'help');
	const showRegisterPasskey = Boolean(user && isHelpProfile);
	const showViewMyProfile = Boolean(user && !isOwnProfile && !isHelpProfile);

	const handleRegisterPasskey = async () => {
		setRegisteringPasskey(true);
		setPasskeyStatus(null);
		try {
			const optRes = await fetch('/api/passkey/add-options', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${token}`,
				},
			});
			const optData = await optRes.json();
			if (!optRes.ok) {
				setPasskeyStatus({ type: 'error', message: optData.error || 'Could not start passkey registration.' });
				play({ id: 'error' });
				return;
			}

			const attestationResponse = await startRegistration({ optionsJSON: optData.options });

			const verifyRes = await fetch('/api/passkey/add-verify', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${token}`,
				},
				body: JSON.stringify({
					challengeToken: optData.challengeToken,
					attestationResponse,
				}),
			});
			const verifyData = await verifyRes.json();
			if (!verifyRes.ok) {
				setPasskeyStatus({ type: 'error', message: verifyData.error || 'Passkey registration failed.' });
				play({ id: 'error' });
				return;
			}

			setPasskeyStatus({
				type: 'success',
				message: verifyData.notified
					? 'New passkey registered. You can use it to sign on from this site, and a confirmation email is on its way.'
					: 'New passkey registered. You can use it to sign on from this site.',
			});
			play({ id: 'register' });
		} catch (err) {
			setPasskeyStatus({
				type: 'error',
				message: err?.name === 'NotAllowedError'
					? 'Passkey creation was cancelled.'
					: 'Passkey registration failed. Try again.',
			});
			play({ id: 'error' });
		} finally {
			setRegisteringPasskey(false);
		}
	};

	return (
		<div className="buddy-info window">
			<div className="title-bar">
				<span className="title-bar-text">
					<span>{profile ? `Buddy Info: ${profile.screen_name}` : 'Buddy Info'}</span>
				</span>
				<div className="title-bar-controls">
					<button aria-label="Minimize" onClick={() => play({id: 'msgRcv'})}></button>
					<button aria-label="Maximize" onClick={() => play({id: 'msgSnd'})}></button>
					<button aria-label="Help" onClick={() => { play({id: 'doorOpen'}); navigate('/profile/help'); }}></button>
					<button aria-label="Close" onClick={() => { play({id: 'doorClose'}); clearLookup(); }}></button>
				</div>
			</div>
			<div className="window-body">
				<div className="get-info">
					<p id="get-screename-label">Enter the screen name of the user you wish to get info about:</p>
					<form onSubmit={handleSearch} style={{ display: 'flex' }}>
						<div className="get-screename-field" ref={fieldRef}>
							<input
								type="text"
								value={query}
								onChange={handleQueryChange}
								name="get-screename"
								className="get-screename"
								autoComplete="off"
								ref={inputRef}
								aria-labelledby="get-screename-label"
							/>
							<button
								type="button"
								className="recent-profiles-toggle"
								onClick={() => setIsOpen((open) => !open)}
								disabled={recents.length === 0}
								aria-expanded={isOpen}
								aria-controls={isOpen ? 'recent-profiles' : undefined}
								aria-label="Recently viewed profiles"
								ref={toggleRef}
							></button>
							{/* Rendered whenever the dropdown is open, even with nothing to
							    show, so aria-expanded and aria-controls always describe what
							    is really on screen. */}
							{isOpen && (
								<ul className="suggestions-list" id="recent-profiles">
									{suggestions.length === 0 ? (
										<li className="suggestions-empty">No matching profiles</li>
									) : (
										suggestions.map((s) => (
											<li key={s}>
												<button type="button" onClick={() => selectSuggestion(s)}>
													{s}
												</button>
											</li>
										))
									)}
								</ul>
							)}
						</div>
						<button type="submit" disabled={loading}>OK</button>
					</form>
				</div>
				{error && <p style={{ color: '#c00', fontSize: 12 }}>{error}</p>}
				{profile && (
					<>
						<hr />
						<div className="details">
							<p><strong>Warning Level:</strong> 0%</p>
							<p><strong>Online time:</strong> {getOnlineTime(profile.created_at)}</p>
						</div>
						<hr />
						<div className="profile-container">
							<p><strong>Personal Profile:</strong></p>
							<div className="profile" style={profile.bgcolor ? { backgroundColor: profile.bgcolor } : {}} dangerouslySetInnerHTML={{ __html: sanitizeProfile(profile.profile || '<em>No profile set.</em>') }} />
						</div>
					</>
				)}
				{!profile && !error && (
					<>
						<hr />
						<div style={{ textAlign: 'center', padding: '20px 0', fontSize: 12 }}>
							<p>{user ? 'Search for a profile to view buddy info.' : 'Search for a profile or sign on to manage your profile.'}</p>
						</div>
					</>
				)}
				<hr />
				<div className="buddy-info-footer">
					{user && (
						<span>
							Signed in as <strong>{user.screen_name}</strong>
						</span>
					)}
					{showRegisterPasskey && passkeyStatus && (
						<p style={{ color: passkeyStatus.type === 'error' ? '#c00' : '#060', fontSize: 12, margin: '4px 0' }}>
							{passkeyStatus.message}
						</p>
					)}
					<div className="buddy-info-footer-actions">
						{isOwnProfile && (
							<button onClick={() => navigate('/edit')}>Edit My Profile</button>
						)}
						{showRegisterPasskey && (
							<button onClick={handleRegisterPasskey} disabled={registeringPasskey}>
								{registeringPasskey ? 'Registering Passkey...' : 'Register New Passkey'}
							</button>
						)}
						{showViewMyProfile && (
							<button onClick={() => navigate(`/profile/${encodeURIComponent(user.screen_name)}`)}>View My Profile</button>
						)}
						{user ? (
							<button onClick={() => { play({id: 'disconnect'}); logout(); }}>Log Out</button>
						) : (
							<>
								<button onClick={() => navigate('/login')}>Sign On</button>
								<button onClick={() => navigate('/register')}>Get a Screen Name</button>
							</>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
